'use strict';

import _ from 'underscore';
import mediator from '../../js/mediator';
import config from '../../js/config';
import utilities from '../../js/utilities';
import Analytics from '../Analytics/Analytics';
import ProductImpression from './ProductImpression';
import {IMPRESSION_REQUIRED_VIEW_MILLISECONDS} from './ProductImpression';
import { EnableAdobeAnalytics } from '../../utilities/featureFlags';

export default class ProductImpressionList {
  constructor($el, $products, startIndex = 0) {
    this.$el = $el;
    this.el = $el.get(0);
    this.$products = $products;
    this.startIndex = startIndex;
    this.name = this.$el.data('gtmProductImpression');
    this._getProductImpressions();
    this._bindEvents();

    // Check if list is in view immediately
    _.defer(() => this._triggerImpressionCheck());
  }

  /**
   * Check if we are partially in the viewport, in order to check our products.
   *
   * @param {{height: number, width: number}} viewport
   */
  impressionCheck(viewport) {
    // Only consider fully opaque items as impressions
    if (this.$el.css('opacity') !== '1') {
      return this.clearAllTimers();
    }

    // Check if the product list is partially in the viewport
    let isInViewport = utilities.isElementPartiallyInViewport(this.el, null, viewport);

    // Clear timeout of all products in list if they used to be in the viewport
    if (!isInViewport) {
      return this.clearAllTimers();
    }

    // Work out if any of our products are in the viewport
    this.products.forEach(product => product.impressionCheck(viewport));
  }

  /**
   * Get the available product impressions.
   *
   * @private
   */
  _getProductImpressions() {
    //ignore the horizontal scroll bar in the parametrics columns when in product Search
    let ignoreHorizontal = this.$el.hasClass('SearchResults-results');

    this.products = this.$products.map((n, el) => {
      return new ProductImpression(el, this.name, n + this.startIndex, ignoreHorizontal);
    }).get();
  }

  /**
   * Add any additional events.
   *
   * @private
   */
  _bindEvents() {
    mediator.on(config.events.productImpressions.checkListElement, this._checkListElement, this);

    // If it is a tabbed responsive carousel, then we need to listen to pagination events and tab changes
    if (this.$el.hasClass('TabbedResponsiveCarousel')) {
      // Get the tab with the same index as us in the main carousel
      let $tabbedCarousel = this.$el.parents('.TabbedCarousel');
      let index = $tabbedCarousel.find('.TabbedResponsiveCarousel').index(this.$el);

      // Check this list for active impressions after tab change
      $tabbedCarousel.find('.BlackHeader--keyline-button').eq(index).on('click', this._triggerImpressionCheck.bind(this));
    }

    // If it is a tabbed content, then we need to listen to tab changes
    if (this.$el.hasClass('TabbedContent-content')) {
      // Get the tab with the same index as us in the main element
      let $tabbedContent = this.$el.parents('.js-TabbedContent--onPage');
      let index = $tabbedContent.find('.TabbedContent-content').index(this.$el);

      // Check this list for active impressions after tab change
      $tabbedContent.find('.TabbedContent-tab').eq(index).on('click', () => {
        _.delay(() => this._triggerImpressionCheck(), IMPRESSION_REQUIRED_VIEW_MILLISECONDS)
      });
    }

    // Add product click impressions
    this.$products.on('click', 'a:not(.Icon)', this.trackProductClick.bind(this));
  }

  /**
   * Trigger an impression check if the element passed in
   * matches this list
   *
   * @param element
   * @private
   */
  _checkListElement(element) {
    if (element === this.el) {
      this._triggerImpressionCheck();
    }
  }

  _triggerImpressionCheck() {
    mediator.trigger(config.events.productImpressions.check, [this])
  }

  /**
   * Clear all timers if they were active and update inViewport flag.
   */
  clearAllTimers() {
    this.products.forEach(product => {
      if (product.inViewport) {
        clearTimeout(product.viewportTimer);
        product.inViewport = false;
      }
    });
  }

  /**
   * Get all the product elements in a list jQuery object.
   *
   * @static
   * @param {jQuery} $list
   * @returns {jQuery}
   */
  static getProductsInListElement($list) {
    return $list.find('[data-part-id]').not('.SearchResults-column').not('a.BomModal-opener');
  }

  trackProductClick(e) {
    let index = this.$products.index(e.delegateTarget);
    const origin = window.location.origin;
    const queryString = window.location.search;
    const params = new URLSearchParams(queryString);
    const search = params.get('q');
    const searchType = search ? 'generic' : 'parametric';
    // Generate the analytics impression information
    let products = this.products[index].getDatalayerImpression();
    let list = this.name;

    let impression = {
      click: {
        actionField: { list, action: 'click'},
        products: [products]
      }
    };
    let abobeProductSearchClickData = {
      event: config.analytics.eventTags.productSearchClick,
      searchTerm: search,
      searchType: searchType,
      searchLinkText: products.name,
      searchLinkHref: origin + this.products[index].el.dataset.partUrl,
      products: [
        {
          SKU: products.id,
          name: products.name
        }]
    }
    if (EnableAdobeAnalytics.isEnabled()) {
      Analytics.setOnAdobeDataLayer(abobeProductSearchClickData);
    } else {
      Analytics.ecommercePushWithCurrency(config.analytics.eventTags.productListClickEcommerce, impression);
    }
  }
}



// WEBPACK FOOTER //
// ./assets/components/ProductImpressions/ProductImpressionList.js